/**
 * Инлайновые SVG-иконки интерфейса — единый набор взамен эмодзи.
 *
 * ПОЧЕМУ ИНЛАЙН, А НЕ ЗАВИСИМОСТЬ (lucide-react и т.п.): иконок нужно
 * около дюжины, а библиотека тащит в package.json зависимость, которую
 * придётся обновлять, ради набора, который помещается в один файл.
 * Кроме того, у проекта осознанно нет UI-фреймворка (см. шапку styles.ts) —
 * иконочная библиотека была бы первым шагом туда. Геометрия ниже следует
 * отраслевому стандарту (Lucide/Feather, stroke 2px, скруглённые концы),
 * поэтому иконки читаются привычно, а не самодельно.
 *
 * ПОЧЕМУ stroke + currentColor: одна иконка работает на любом фоне и в
 * любом состоянии (обычная/hover/disabled/active) без второй версии —
 * цвет приходит из CSS color родителя. Эмодзи так не умели: 📹 на красной
 * кнопке оставался цветным пятном, а 🔔 в тёмной теме исчезал бы.
 *
 * ПРАВИЛО: размер задаётся через prop size (по умолчанию 18 — размер
 * иконки в кнопке 32px). Растягивать через font-size больше нельзя:
 * это SVG, а не текст.
 */
import type { CSSProperties } from "react";

interface IconProps {
  /** Ширина и высота в px. 18 — умолчание кнопок шапки; 20 — композер. */
  size?: number;
  style?: CSSProperties;
  /** Доступное имя, если иконка несёт смысл без подписи рядом. */
  title?: string;
}

function base(props: IconProps) {
  return {
    width: props.size ?? 18,
    height: props.size ?? 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    style: { display: "block", flex: "0 0 auto", ...props.style },
    "aria-hidden": props.title ? undefined : true,
    role: props.title ? "img" : undefined,
  };
}

/**
 * Две сходящиеся стрелки — «свернуть звонок в окошко».
 *
 * Антон 12.09 10:15 (голосом): «надо убедиться, что в той же самой комнате
 * есть кнопка для того, чтобы уменьшить, типа две сходящиеся стрелочки,
 * чтобы перевести звонок в режим вот этого вот окошка».
 *
 * ПОЧЕМУ НЕ ИКОНКА СООБЩЕНИЯ: кнопка там была (call-compose), но с
 * «облачком» — Антон на скриншоте её не опознал как сворачивание и
 * сообщил, что писать в своей комнате невозможно. Иконка должна называть
 * ДЕЙСТВИЕ (уменьшить окно), а не его повод (написать).
 */
export function IconMinimize(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      {/* Стрелка в левый нижний угол */}
      <polyline points="10 4 10 10 4 10" />
      <line x1="3" y1="3" x2="10" y2="10" />
      {/* Стрелка в правый верхний угол */}
      <polyline points="14 20 14 14 20 14" />
      <line x1="21" y1="21" x2="14" y2="14" />
    </svg>
  );
}

/** Трубка — аудиозвонок / входящий звонок. */
export function IconPhone(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/** Трубка снятая/завершить — красная кнопка «выйти из звонка». */
export function IconPhoneOff(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67M4.72 4.72a19.79 19.79 0 0 1-.6-.6A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="23" y1="1" x2="1" y2="23" />
    </svg>
  );
}

/** Видеокамера — видеозвонок / камера включена. */
export function IconVideo(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="m22 8-6 4 6 4V8Z" />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </svg>
  );
}

/** Перечёркнутая камера — камера выключена. */
export function IconVideoOff(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** Микрофон включён. */
export function IconMic(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

/** Микрофон выключен. */
export function IconMicOff(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2" />
      <path d="M5 10v2a7 7 0 0 0 12 5" />
      <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

/**
 * «Поделиться» — отраслевой стандарт: квадрат со стрелкой вверх
 * (Apple share glyph, он же «export» в Lucide). Постановка Антона 16:16
 * буквально: «поделиться = квадрат со стрелкой». Прежняя «↗» читалась
 * как «открыть в новой вкладке», а не как «поделиться».
 */
export function IconShare(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

/** Скрепка — прикрепить файл. */
export function IconAttach(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

/** Улыбка — открыть пикер эмодзи. */
export function IconSmile(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  );
}

/** Колокол — уведомления включены. */
export function IconBell(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

/** Перечёркнутый колокол — уведомления приглушены. */
export function IconBellOff(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
      <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
      <path d="M18 8a6 6 0 0 0-9.33-5" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** Скачать (стрелка вниз на полку). */
export function IconDownload(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

/** Закрыть — крестик. */
export function IconClose(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/**
 * Лупа — поиск (декоративная иконка в поле). */
export function IconSearch(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/**
 * Участники — панель/список участников звонка (STEP8 эт.4, контрол
 * звонка по образцу Teams). Геометрия — стандартный «users» из
 * Lucide/Feather: две головы + два силуэта плеч. В наборе иконок её не
 * было — все люди рисовались текстом; задача этапа 4 явно требует.
 */
export function IconUsers(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** Троеточие — «ещё». */
export function IconMore(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

/** Стрелка влево — назад (навигация в лайтбоксе). */
export function IconChevronLeft(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

/** Стрелка вверх — открыть меню выбора устройства (задача 2026-09-13).
    Меню контролов звонка открывается ВВЕРХ (контролы снизу экрана),
    поэтому шеврон смотрит вверх — как в Teams/Slack у кнопок микрофона
    и камеры. */
export function IconChevronUp(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

/** Стрелка вправо — вперёд (навигация в лайтбоксе). */
export function IconChevronRight(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** Плюс — открыть меню вложений (фото/видео, файл). */
export function IconPlus(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/** Самолётик — отправить сообщение. */
export function IconSend(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      {/* СТРЕЛКА ВВЕРХ, а не «телеграмовский самолётик» (Антон 19:58:
          «замени телеграмовский самолётик на стрелочку красивую»).
          Вверх, а не вправо: сообщение уходит ВВЕРХ в ленту — жест
          совпадает с направлением, куда улетит пузырь. Плюс полигон
          самолётика при stroke-обводке 2px на 20px мельчил и выглядел
          грязным пятном, а у стрелки три чистые линии. */}
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}

/**
 * Домик — вкладка Home на вертикальном рельсе.
 *
 * Раньше там стоял текстовый символ «⌂» размером font.small: в кружке
 * 40px он выглядел крошечной закорючкой в центре пустого круга.
 * Антон 19:58: «иконку домика слева наверху сделай больше на весь
 * кружок». SVG масштабируется размером, текстовый глиф — нет.
 */
export function IconHome(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5.5 9.5V20h13V9.5" />
    </svg>
  );
}

/** Галочка — сохранить изменения (режим редактирования). */
export function IconCheck(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Картинка — фото/видео в меню вложений. */
export function IconImage(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </svg>
  );
}

/** Документ — файл в меню вложений. */
export function IconFile(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
  );
}

/**
 * Речевой пузырь — «написать в комнату» на экране звонка (дополнение
 * Антона 2026-09-12 09:20: кнопка сворачивает звонок в мини-фрейм и
 * открывает обычный композер). Геометрия — стандартный message-circle
 * из Lucide: окружность с хвостиком внизу слева.
 */
export function IconMessage(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}

/* --- Личный кабинет (2026-09-12) --------------------------------------- */

/** Фотоаппарат — «Изменить фото» в личном кабинете. */
export function IconCamera(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

/** Монитор — раздел «Устройства». */
export function IconDevices(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

/** Карандаш — переименовать устройство (раздел «Устройства», 2026-09-12). */
export function IconEdit(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

/** Корзина — удалить устройство (раздел «Устройства», 2026-09-12).
    Геометрия — стандартный trash из Lucide/Feather. */
export function IconTrash(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <line x1="10" y1="11" x2="10" y2="17" />
      <line x1="14" y1="11" x2="14" y2="17" />
    </svg>
  );
}

/** Глобус — раздел «Язык» в личном кабинете (i18n, шаг 2). */
export function IconGlobe(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

/** Палитра — раздел «Оформление». */
export function IconPalette(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <circle cx="13.5" cy="6.5" r=".5" />
      <circle cx="17.5" cy="10.5" r=".5" />
      <circle cx="8.5" cy="7.5" r=".5" />
      <circle cx="6.5" cy="12.5" r=".5" />
      <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
    </svg>
  );
}

/** Силуэт человека — аватар-заглушка и разделы про людей. */
export function IconUser(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/**
 * Монитор со стрелкой вверх — «демонстрация экрана» (screen share,
 * Антон 12.09 12:28). Геометрия — стандартный screen-share из Lucide:
 * монитор + стрелка, отдающая содержимое наружу.
 */
export function IconScreenShare(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="m17 8 5-5" />
      <path d="M17 3h5v5" />
    </svg>
  );
}

/**
 * Перечёркнутый монитор — демонстрация экрана НЕ идёт. Тот же язык,
 * что у IconMicOff/IconVideoOff: перечёркнутая иконка = выключено,
 * кнопка живая.
 */
export function IconScreenShareOff(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
      <path d="m17 8 5-5" />
      <path d="M17 3h5v5" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

/** Динамик (логика кнопки «Динамик» в панели звонка, эталон iOS
    CallView.swift ControlButton). */
export function IconSpeaker(p: IconProps) {
  return (
    <svg {...base(p)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

/** Перевернуть камеру фронт/тыл (кнопка «Перевернуть камеру» в панели
    звонка, эталон iOS — cameras.arrow.triangle.2.circlepath). */
export function IconCameraFlip(p: IconProps) {
  return (
    <svg {...base(p)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

/** Square stop glyph for an active MediaRecorder. */
export function IconStop(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <rect x="6" y="6" width="12" height="12" rx="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Person with a plus — "add participant" (call controls, Anton 2026-09-17). */
export function IconUserPlus(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="17" y1="11" x2="23" y2="11" />
    </svg>
  );
}

/**
 * Шестерёнка — «Настройки» (правка Антона 2026-09-20 19:17, п.2):
 * вход в настройки из мобильной шапки списка, рядом с «+». Рисунок —
 * классическое колесо с восемью зубьями (feather settings), чтобы на
 * тач-экране читалось с первого взгляда, без вариаций.
 */
export function IconSettings(p: IconProps) {
  return (
    <svg {...base(p)}>
      {p.title && <title>{p.title}</title>}
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
